import { PropTypes } from 'prop-types';
import React, { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { Loader } from '@ge/components/loader';
import { ConditionsKpiCategoryDefs, KpiCategoryDefs, Placeholders } from '@ge/models/constants';

import { NoDataAvailable } from '../../no-data-available/no-data-available';
import { ListTable } from '../../tables/list-table';
import { ReportsContext } from '../../../context/reports-context';
import { PerformanceStateKeys, PerformanceSummaryFields } from '../performance-summary-widget/performance-summary-config';

const DefaultRowOrder = {
  [KpiCategoryDefs.PRODUCTION_ACTUAL]: 0,
  [KpiCategoryDefs.CAPACITY_FACTOR]: 1,
  [KpiCategoryDefs.AVAILABILITY_CONTRACT]: 2,
  operatingHours: 3,
  [ConditionsKpiCategoryDefs.WIND]: 4,
  [KpiCategoryDefs.LOST_PRODUCTION]: 5,
};

const getDisplayValue = (value, language) =>
  new Intl.NumberFormat(language, {
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
  }).format(value);

const ListContainer = styled.div`
  height: 100%;
`;

const LoaderContainer = styled.div`
  height: 100%;
  position: relative;
  width: 100%;
`;

const StyledHeader = styled.div`
  border-bottom: 1px solid ${({ theme }) => theme.createReport.widget.headerBorderColor};
  padding-bottom: 8px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 15px;
  text-transform: uppercase;
  color: ${({ theme }) => theme.createReport.widget.headerTextColor};
`;

export const ListWidget = ({ data, headerContent, id, name, isLoading, isPlaceholder }) => {
  const { t } = useTranslation(['reporting.widgets']);

  const { getWidgetState } = useContext(ReportsContext);
  const configState = getWidgetState(id, PerformanceStateKeys.CONFIG_STATE);

  const rows = useMemo(() => {
    if (!data) {
      return null;
    }

    const entries = Object.entries(data);

    return entries
      .reduce((mapped, [key, _value]) => {
        if (_value) {
          const { units, value } = _value;
          if (key === 'wind') {
            mapped.push({
              id: key,
              title: t(`kpi.${key}`, key),
              units: t(`kpi.units.${units}`, units),
              value: value ? value : Placeholders.DOUBLE_DASH,
            });
          } else {
            mapped.push({
              id: key,
              title: t(`kpi.${key}`, key),
              units: t(`kpi.units.${units}`, units),
              value: value ? getDisplayValue(value) : Placeholders.DOUBLE_DASH,
            });
          }
        }

        return mapped;
      }, [])
      .sort(({ id: a }, { id: b }) => DefaultRowOrder[a] - DefaultRowOrder[b]);
  }, [data, t]);

  const showGroup = configState?.find((group) => group.id === PerformanceSummaryFields.GROUP_ROWS);
  const rowsField = showGroup?.fields?.filter(
    (field) => field.visible === true,
  );

  const displayedRows = rows?.filter(row => rowsField?.find(showRow => showRow.id === row.title));

  if (isLoading) {
    return (
      <>
        <StyledHeader>
          <h4>{name}</h4>
        </StyledHeader>
        <ListContainer>
          <LoaderContainer>
            <Loader />
          </LoaderContainer>
        </ListContainer>
      </>
    );
  }

  if (!data) {
    return <NoDataAvailable />;
  }

  if (rows?.length === 0) {
    return (
      <>
        <StyledHeader>
          <h4>{name}</h4>
        </StyledHeader>
        {!isPlaceholder && (
          <ListContainer>
            <NoDataAvailable />
          </ListContainer>
        )}
      </>
    );
  }

  return (
    <>
      <StyledHeader>
        <h4>{name}</h4>
        {headerContent}
      </StyledHeader>
      {!isPlaceholder && <ListTable data={displayedRows} />}
    </>
  );
};

ListWidget.propTypes = {
  name: PropTypes.string.isRequired,
  data: PropTypes.instanceOf(Object),
  headerContent: PropTypes.node,
  isLoading: PropTypes.bool,
  isPlaceholder: PropTypes.bool,
};

ListWidget.defaultProps = {
  data: {},
  headerContent: null,
};
